import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { createSubscriptionAtom, isEditCreateFormOpenAtom } from '../atoms';
import { useESMetricsCategories, useFetchCustomerAllUsersEmail } from './hooks';
import EditCreateForm from './SubscriptionForm';
import { EditProps } from '../types';
import { useFetchEmailSubscriptionList } from '../list/hooks';
import { Retry } from '@src/components/common';
import { isAdminRoleSelector } from '@src/store/common/selectors';
import useAIAlertMetricsMeta from '@src/components/ai-alerts/hooks/useAIAlertMetricsMeta';

const EmailSubscriptionEdit = ({ emailId = null, createEditLoading = false, form }: EditProps) => {
  const createSubscription = useAtomValue(createSubscriptionAtom);
  const isAdminUser = useSelector(isAdminRoleSelector);
  const isEditCreateFormOpen = useAtomValue(isEditCreateFormOpenAtom);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    return () => {
      form.resetFields();
    };
  }, []);

  const { data: emailSubscriptionsList = [] } = useFetchEmailSubscriptionList(false);

  const subscriptionData = emailSubscriptionsList.find(
    (subscription) => subscription.email === emailId,
  );

  const {
    data: allUsersEmailOptions = [],
    isLoading: usersEmailDataLoading,
    isError: usersEmailDataError,
    refetch: usersEmailDataRefetch,
  } = useFetchCustomerAllUsersEmail(isAdminUser);

  const {
    aiAlertMetricsMeta,
    isFetching: isAlertMetaLoading,
    isError: alertMetaFetchError,
    refetch: alertMetaRefetch,
  } = useAIAlertMetricsMeta(isEditCreateFormOpen);

  const esMetricsMeta = useESMetricsCategories(aiAlertMetricsMeta, searchText);

  const retryFetch = () => {
    usersEmailDataError && usersEmailDataRefetch();
    alertMetaFetchError && alertMetaRefetch();
  };

  return (
    <Retry
      isLoading={usersEmailDataLoading || createEditLoading || isAlertMetaLoading}
      fetchData={retryFetch}
      mountChildAlways={false}
      showError={usersEmailDataError || alertMetaFetchError}
    >
      <EditCreateForm
        type={createSubscription ? 'create' : 'edit'}
        userEmails={allUsersEmailOptions}
        subscriptionData={subscriptionData}
        form={form}
        metricsMeta={esMetricsMeta}
        searchProps={{ searchText, setSearchText }}
      />
    </Retry>
  );
};

export default EmailSubscriptionEdit;
